// Client-side search / filter / sort / paginate over an in-memory array, shaped
// to the same `TResponse<T[]>` envelope the real admin list endpoints return.
// Lets static (endpoint-less) modules drive the standard `useAdminTable` +
// `Pagination` UI unchanged; when the backend lands, the module service swaps
// `queryStaticList(store, params)` for `api.get(endpoints.list(params))`.

import type { TAdminListParams } from '@/core/lib/table-query'
import type { TResponse } from '@/core/types/axios'
import { DEFAULT_PER_PAGE } from '@/core/lib/pagination'

export function queryStaticList<T>(
  source: readonly T[],
  params: TAdminListParams
): TResponse<T[]> {
  const searchField = params.searchField ?? 'name'
  const search = params.search?.trim().toLowerCase()

  let rows = source.map((row) => row as T)

  if (search) {
    rows = rows.filter((row) =>
      String((row as Record<string, unknown>)[searchField] ?? '')
        .toLowerCase()
        .includes(search)
    )
  }

  if (params.filters) {
    for (const [field, values] of Object.entries(params.filters)) {
      if (!values || values.length === 0) continue
      rows = rows.filter((row) => {
        const cell = (row as Record<string, unknown>)[field]
        return values.some((v) => cell === v || String(cell) === String(v))
      })
    }
  }

  if (params.sort) {
    const { field, order } = params.sort
    const dir = order === 'desc' ? -1 : 1
    rows = [...rows].sort((a, b) => {
      const av = (a as Record<string, unknown>)[field]
      const bv = (b as Record<string, unknown>)[field]
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir
      }
      return String(av ?? '').localeCompare(String(bv ?? ''), 'tr') * dir
    })
  }

  const perPage = params.perPage ?? DEFAULT_PER_PAGE
  const page = params.page ?? 1
  const total = rows.length
  const total_page = Math.max(1, Math.ceil(total / perPage))
  const start = (page - 1) * perPage
  const data = rows.slice(start, start + perPage)

  return {
    data,
    meta: { pagination: { total, page, per_page: perPage, total_page } },
    message: 'ok',
  }
}
